import { useLocalStorageBackedState } from './useLocalStorageBackedState';
import { Vessel } from '../components/VesselContext';
import { useState } from 'react';

export type FilterBy = keyof Pick<
  Vessel,
  | 'VesselGroup'
  | 'VesselOwner'
  | 'Fleet'
  | 'StatusDetail'
  | 'VesselName'
  | 'NextPort'
  | 'PreviousPortName'
>;

export type ToggleFilters = Partial<Record<FilterBy, Set<string>>>;

export type TextFilters = Partial<Record<FilterBy, string>>;

export type FilterChangeSingleValueFn = (
  filterType: FilterBy,
  filterValue: string
) => void;

export type FilterChangeSetFn = (
  filterType: FilterBy,
  filterValue: Set<string>
) => void;

interface OptionsProps {
  initialToggleFilterState?: ToggleFilters;
  initialTextFilterState?: TextFilters;
  persist?: boolean;
}

const FILTER_STORAGE_KEY = 'filters';

export function useFilterState({
  initialToggleFilterState = {},
  initialTextFilterState = {},
  persist = true
}: OptionsProps = {}) {
  const [activeToggleFilters, setActiveToggleFilters] =
    useLocalStorageBackedState<ToggleFilters>({
      key: FILTER_STORAGE_KEY,
      initialValue: initialToggleFilterState,
      persist
    });

  const [activeTextFilters, setActiveTextFilters] = useState<TextFilters>(
    initialTextFilterState
  );

  /**
   *  This function is used to change a single value in filter type
   */
  const onFilterChangeSingleValue: FilterChangeSingleValueFn = (
    filterType,
    filterValue
  ) => {
    // Create a new set from existing values for the type
    const newValue = new Set(activeToggleFilters[filterType]);

    // If the filter is already selected, unselect it or add it if not selected
    newValue.has(filterValue)
      ? newValue.delete(filterValue)
      : newValue.add(filterValue);

    // Call the fn to change a set when the set is created
    onFilterChangeSet(filterType, newValue);
  };

  /*
   * This function takes in a set of values and updates the state for the filter type
   */
  const onFilterChangeSet: FilterChangeSetFn = (filterType, filterValue) => {
    setActiveToggleFilters((prev) => {
      // If the set has values add it to state
      if (filterValue.size !== 0) {
        return {
          ...prev,
          [filterType]: filterValue
        };
      }

      // Remove any empty keys so the state is always clean and don't have `type: {}`
      const { [filterType]: removedKey, ...remainingActiveToggleFilters } =
        prev;

      return {
        ...remainingActiveToggleFilters
      };
    });
  };

  const onTextFilterChangeValue: FilterChangeSingleValueFn = (
    filterType,
    filterValue
  ) => {
    setActiveTextFilters((prev) => {
      if (filterValue && filterValue.length > 0) {
        return {
          ...prev,
          [filterType]: filterValue
        };
      }

      const { [filterType]: removedKey, ...remainingActiveTextFilters } = prev;

      return {
        ...remainingActiveTextFilters
      };
    });
  };

  const resetAllFilters = () => setActiveToggleFilters({});

  return {
    activeToggleFilters,
    setActiveToggleFilters,
    activeTextFilters,
    onFilterChangeSet,
    onFilterChangeSingleValue,
    onTextFilterChangeValue,
    resetAllFilters
  };
}
